"use client";

import posthog from "posthog-js";
import type { PostHog } from "posthog-js";

/**
 * Client-side PostHog API.
 *
 * Public surface:
 *   import posthog from 'posthog-js'                       // raw client (unchanged)
 *   import { capturePosthogEvent } from '@/lib/posthog'    // dual-fire capture
 *
 * Server-side capture lives in `@/lib/posthog-server` to keep
 * `posthog-node` out of the client bundle. See that file for details.
 */

type CaptureOptions = {
  sendToServer?: boolean;
  posthogOptions?: Record<string, unknown>;
};

const ANALYTICS_ENDPOINT = "/api/analytics";

function generateEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 generator
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function sendToServer(payload: {
  event: string;
  properties: Record<string, unknown>;
  distinctId?: string;
  eventId: string;
  timestamp: string;
}) {
  if (typeof window === "undefined") return;

  const body = JSON.stringify({
    event: payload.event,
    properties: payload.properties,
    distinctId: payload.distinctId,
    eventId: payload.eventId,
    // The browser copy and this relayed copy have to agree on the timestamp
    // or PostHog stores both. See capturePosthogEvent below.
    timestamp: payload.timestamp,
  });

  try {
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ANALYTICS_ENDPOINT, blob);
    } else {
      await fetch(ANALYTICS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body,
        keepalive: true,
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[PostHog] Server capture failed", error);
    }
  }
}

export function capturePosthogEvent(
  posthog: PostHog | undefined | null,
  event: string,
  properties: Record<string, unknown> = {},
  options: CaptureOptions = {}
) {
  if (!event) return;

  const eventId =
    (typeof properties.$insert_id === "string" && properties.$insert_id) ||
    generateEventId();

  const payload = {
    ...properties,
    $insert_id: eventId,
  };

  // One timestamp for both copies of a dual-fired event.
  //
  // A sendToServer event is captured twice on purpose: from the browser,
  // because the relay can be cancelled, and from the server, because ad
  // blockers eat the browser call. PostHog is meant to collapse the pair on
  // $insert_id, which both copies already carried. It never did, because that
  // dedup also requires the timestamps to match and the relay stamped its own
  // on arrival, roughly 363ms later. Every dual-fired event was therefore
  // stored twice, and the AAARRR dashboard read about 2x reality across the
  // board: 8,385 quiz_completed events against 4,303 results actually saved.
  //
  // Sharing one timestamp fixes the pair. The server still refuses to take it
  // on trust; see the clamp in /api/analytics.
  const capturedAt = new Date();

  let distinctId: string | undefined;
  try {
    distinctId = posthog?.get_distinct_id?.();
    posthog?.capture(event, payload, {
      ...options.posthogOptions,
      timestamp: capturedAt,
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[PostHog] Browser capture failed", error);
    }
  }

  if (options.sendToServer) {
    void sendToServer({
      event,
      properties: payload,
      distinctId,
      eventId,
      timestamp: capturedAt.toISOString(),
    });
  }

  return eventId;
}

export default posthog;
